/**
 * PropertyGrid — the generic, schema-driven Eigenschaften renderer of
 * the farm twin explorer (design §3.3).
 *
 * Ontology-driven, data-tolerant (design principle 1): merged-schema
 * fields render first in schema order with their German labels; runtime
 * attributes missing from the schema still render with a prettified key.
 * Null/empty values hide behind the "leere Felder anzeigen" toggle —
 * never rendered as dashes-as-content (principle 2).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, History, Pencil } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@hofsync/design-system';
import type { EntitySourceDoc, LenseField } from '../../../services/twinService';
import { classifyValue, formatScalar, prettifyKey, toMiniTable } from '../formatValue';
import { normalizeAsOf } from './asof';
import InlineValueEditor from './InlineValueEditor';
import { ALIAS_ATTRIBUTE } from './nameAliases';
import {
    DENSITY_ATTRIBUTE,
    DENSITY_COMPANION_ATTRIBUTES,
    type PlantingDensity,
    formatDensity,
    readPlantingDensity,
} from './plantingDensity';
import { DEFAULT_SOURCE_SYSTEMS, resolveProvenanceSource, type SourceSystemEntry } from './provenanceSource';
import { linkTypeLabel } from '../../../services/linkTypeLabels';
import './PropertyGrid.css';

/** Per-attribute as-of metadata (#1913 C): whether reverting the attribute's
 * last change reveals a real prior value (drives the affordance) and the raw
 * ``source_id`` of the transition that governs the displayed value (#2791 —
 * in BOTH the live and the historical view). */
export interface AttributeHistoryMeta {
    canReveal: boolean;
    provenanceSourceId: string | null;
}

/** " · Gültigkeit" for a provenance document that asserts a link type, else nothing. */
function docLinkTypes(docsById: Map<string, EntitySourceDoc> | undefined, docId: string): string {
    const types = docsById?.get(docId)?.link_types ?? [];
    return types.length ? ` · ${types.map(linkTypeLabel).join(', ')}` : '';
}

/** Keys never shown in the grid: infra columns + doc provenance
 * (documents get their own section) + discriminators. */
const INTERNAL_KEYS = new Set([
    'entity_id',
    'entity_type',
    'ontology_version',
    'created_at',
    'updated_at',
    'source_doc_ids',
    'source_doc_id',
    'id',
    'name', // shown as the object title
    'type',
    'properties',
    'documents',
    // FR-018.7 — aliases live in the Sources section, never in the grid.
    ALIAS_ATTRIBUTE,
]);

/** The crop-cycle attribute that names the crop term (FR-018.7 §8). */
const CROP_ATTRIBUTE = 'crop';

/** HOF-626 — a queued edit awaiting the refetched row. */
export interface PendingEdit {
    value: unknown;
    since: number;
    stale: boolean;
}

/** HOF-626 — inline-edit wiring. Props, not hooks: the grid is rendered bare by its suites. */
export interface PropertyGridEditing {
    canEdit: (row: { key: string; field?: LenseField }) => boolean;
    /** Reject → the message is shown inline and the editor stays open. */
    onSave: (key: string, value: unknown) => Promise<void>;
    pending?: ReadonlyMap<string, PendingEdit>;
    /** A save that rejects AFTER its editor was cancelled/replaced has no inline slot left. */
    onOrphanedError?: (key: string, err: unknown) => void;
}

export interface PropertyGridProps {
    properties: Record<string, unknown>;
    fields?: LenseField[];
    /**
     * Attribute keys rendered elsewhere (FK fields shown in the
     * relations panel) — suppressed here to avoid double display.
     */
    suppressKeys?: string[];
    /**
     * German labels for enum-ish values, keyed field id → raw value →
     * label (e.g. agreement_type: sales_agreement → "Verkauf", sourced
     * from the lens feed_groups). Unmapped values render raw.
     */
    valueLabels?: Record<string, Record<string, string>>;
    /**
     * #1913 C — the active as-of instant, or null for the live view. When
     * set, the grid renders reconstructed (historical) values and surfaces
     * each changed attribute's provenance one hop away.
     */
    asOf?: string | null;
    /** #1913 C — per-attribute value-history metadata (affordance + provenance). */
    historyByAttribute?: Map<string, AttributeHistoryMeta>;
    /** #1913 C — reveal an attribute's previous value (jumps the as-of stepper). */
    onAttributeHistory?: (attribute: string) => void;
    /** #1913 C — preview a provenance document (one-hop from a historical value). */
    onPreviewDocument?: (docId: string) => void;
    /**
     * #2791 — the entity's LINKED source documents, keyed by document id.
     * Decides whether an attribute's ``source_id`` is a previewable document
     * or a source-system token; costs no request of its own (the Belege panel
     * already fetches this set).
     */
    docsById?: Map<string, EntitySourceDoc>;
    /**
     * #3385 — the source-system registry that turns a raw ``source_id`` token
     * into a badge label. Optional, defaulting to ``DEFAULT_SOURCE_SYSTEMS``
     * (the five families this file used to freeze into the bundle).
     *
     * ⚠️ A PROP, deliberately not a hook. ``useSourceSystems`` needs a
     * ``QueryClientProvider``, and this component is rendered bare by its own
     * suites and reachable from surfaces that mount none — a fetch in here
     * would throw "No QueryClient set". ``ObjectView`` owns the fetch; the
     * grid stays a pure render of what it is handed.
     */
    sourceSystems?: ReadonlyArray<SourceSystemEntry>;
    /**
     * FR-018.7 §8 — when given, a crop cycle's `crop` value renders as a link to the vocabulary
     * term page. The caller passes it only for `crop_cycle` objects.
     */
    onOpenTerm?: (vocabulary: 'crop', term: string) => void;
    /** HOF-626 — when given (and not in as-of mode), editable rows get a pencil badge. */
    editing?: PropertyGridEditing;
}

interface GridRow {
    key: string;
    label: string;
    value: unknown;
    kind: ReturnType<typeof classifyValue>;
    field?: LenseField;
}

/**
 * Language-aware ontology description pick (#2076 follow-up).
 *
 * The ontology's ``ai_context.description`` prose is German; EN text
 * (``description_en``) exists per-field only once the ontology gains it.
 * English UI therefore serves ``description_en`` when present and falls
 * back to the German ``description`` — never machine-translated, never
 * an empty tooltip (undefined ⇒ no badge).
 */
export function pickFieldDescription(
    field: Pick<LenseField, 'description' | 'description_en'> | undefined,
    language: string | undefined,
): string | undefined {
    if (!field) return undefined;
    const raw =
        language?.toLowerCase().startsWith('en') && field.description_en
            ? field.description_en
            : field.description;
    // The ontology prose carries reST ``literal`` markup; the tooltip is plain text.
    return raw ? raw.replace(/`+/g, '') || undefined : undefined;
}

function buildRows(
    properties: Record<string, unknown>,
    fields: LenseField[],
    suppress: Set<string>,
): GridRow[] {
    const rows: GridRow[] = [];
    const seen = new Set<string>();
    for (const field of fields) {
        // Schema-known keys never resurface through the unknown-attribute tail
        // — a panel-hidden field (e.g. the IBAN fragment attrs) is hidden, not
        // demoted to "attribute the schema doesn't know".
        seen.add(field.id);
        if (field.panel === false) continue;
        if (suppress.has(field.id) || INTERNAL_KEYS.has(field.id)) continue;
        const value = properties[field.id];
        rows.push({
            key: field.id,
            label: field.name,
            value,
            kind: classifyValue(value),
            field,
        });
    }
    // Data-tolerant tail: attributes the schema doesn't know yet.
    for (const [key, value] of Object.entries(properties)) {
        if (seen.has(key) || suppress.has(key) || INTERNAL_KEYS.has(key)) continue;
        // #2807 — the density's unit and estimated flag are rendering metadata the
        // density row prints itself, never rows of their own. The ontology already
        // marks them ``panel: false``, which covers them once the lens schema is
        // loaded; this covers the window where it is not (a rolling deploy, or a
        // deep-linked entity fetched before the bootstrap lens arrives), because the
        // tail would otherwise surface a raw "Current Planting Density Unit" row.
        if (DENSITY_COMPANION_ATTRIBUTES.includes(key)) continue;
        // FK-looking keys without schema stay out of the grid — they are
        // ids, not information, and the relations panel owns references.
        if (key.endsWith('_id')) continue;
        rows.push({ key, label: prettifyKey(key), value, kind: classifyValue(value) });
    }
    return rows;
}

/**
 * #2807 — the Pflanzdichte, which the generic renderer cannot serve at all.
 *
 * `formatScalar` would print a bare `7,8`, and a bare density is not a weaker
 * answer than a labelled one — it is an ambiguous one. Berries read Pfl/m² and
 * arable seed reads kg/ha; those differ by four orders of magnitude, so the number
 * alone cannot be sanity-checked by the farmer it is shown to. (The same trap
 * #2844 avoided by refusing to route a `kg` basis through the normalised quantity,
 * where 5,00 €/dt silently reads as 0,05 €/dt.)
 *
 * So: with a unit → "7,8 Pfl/m²"; without one → *unbekannt*, never the number.
 * The raw figure rides the `title` so an operator debugging a row can still see it
 * without it ever being PRESENTED as a density.
 *
 * A DERIVED density is always badged. The badge is the same `property-grid__source`
 * chip shape #2791/#2842 use — a different colour token, no new interactive element
 * and therefore no new focus ring (the #2722 gate enumerates this stylesheet at
 * exactly two, and this row adds none).
 */
function DensityCell({ density }: { density: PlantingDensity }) {
    if (!density.unit) {
        return (
            <span
                className="property-grid__empty-value"
                title={`Wert ohne Einheit: ${density.value.toLocaleString('de-DE')}`}
            >
                unbekannt
            </span>
        );
    }
    return (
        <span className="property-grid__value">
            {formatDensity(density)}
        </span>
    );
}

function ValueCell({
    row,
    valueLabels,
    properties,
    onOpenTerm,
}: {
    row: GridRow;
    valueLabels?: Record<string, Record<string, string>>;
    properties: Record<string, unknown>;
    onOpenTerm?: (vocabulary: 'crop', term: string) => void;
}) {
    if (row.key === DENSITY_ATTRIBUTE) {
        // Read BEFORE the empty check: a density whose unit is missing must render
        // *unbekannt* rather than the em dash, because the twin does hold a number —
        // "nothing recorded" and "recorded but uninterpretable" are different answers.
        const density = readPlantingDensity(properties);
        if (density) return <DensityCell density={density} />;
        return <span className="property-grid__empty-value">—</span>;
    }
    if (row.kind === 'empty') return <span className="property-grid__empty-value">—</span>;
    if (onOpenTerm && row.key === CROP_ATTRIBUTE && typeof row.value === 'string' && row.value.trim()) {
        const term = row.value.trim();
        return (
            <button type="button" className="property-grid__term-link" onClick={() => onOpenTerm('crop', term)}>
                {term}
            </button>
        );
    }
    const mapped = typeof row.value === 'string' ? valueLabels?.[row.key]?.[row.value] : undefined;
    if (mapped) return <span className="property-grid__value">{mapped}</span>;
    if (row.kind === 'array-object') {
        const table = toMiniTable(row.value as Array<Record<string, unknown>>);
        return (
            <div className="property-grid__table-scroll">
                <table className="property-grid__table">
                    <thead>
                        <tr>
                            {table.columns.map((c) => (
                                <th key={c}>{prettifyKey(c)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {table.rows.map((cells, i) => (
                            <tr key={i}>
                                {cells.map((cell, j) => (
                                    <td key={j}>{cell}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }
    if (row.kind === 'array-scalar') {
        return (
            <span className="property-grid__chips">
                {(row.value as unknown[]).map((v, i) => (
                    <span key={i} className="property-grid__chip">
                        {formatScalar(v, row.key, row.field)}
                    </span>
                ))}
            </span>
        );
    }
    if (row.kind === 'object') {
        const entries = Object.entries(row.value as Record<string, unknown>).filter(
            ([, v]) => classifyValue(v) !== 'empty',
        );
        return (
            <span className="property-grid__nested">
                {entries.map(([k, v]) => (
                    <span key={k} className="property-grid__nested-row">
                        <span className="property-grid__nested-key">{prettifyKey(k)}</span>
                        {formatScalar(v, k)}
                    </span>
                ))}
            </span>
        );
    }
    return <span className="property-grid__value">{formatScalar(row.value, row.key, row.field)}</span>;
}

export const PropertyGrid: React.FC<PropertyGridProps> = ({
    properties,
    fields = [],
    suppressKeys = [],
    valueLabels,
    asOf = null,
    historyByAttribute,
    onAttributeHistory,
    onPreviewDocument,
    docsById,
    sourceSystems = DEFAULT_SOURCE_SYSTEMS,
    onOpenTerm,
    editing,
}) => {
    const { t, i18n } = useTranslation();
    const [showEmpty, setShowEmpty] = useState(false);
    // HOF-626 — one editor session at a time; a save closes only its own session.
    const [editSession, setEditSession] = useState<{ key: string; session: number } | null>(null);
    const sessionRef = useRef(0);
    const openSessionRef = useRef<{ key: string; session: number } | null>(null);
    openSessionRef.current = editSession;
    // Row key whose badge should take focus once its editor has unmounted (Escape / saved only).
    const refocusKeyRef = useRef<string | null>(null);
    const badgeRefs = useRef(new Map<string, HTMLButtonElement>());
    const onCancelEdit = useCallback((reason: 'escape' | 'outside') => {
        if (reason === 'escape') refocusKeyRef.current = openSessionRef.current?.key ?? null;
        setEditSession(null);
    }, []);
    useEffect(() => {
        if (editSession === null && refocusKeyRef.current) {
            badgeRefs.current.get(refocusKeyRef.current)?.focus();
        }
        if (editSession === null) refocusKeyRef.current = null;
    }, [editSession]);
    const isHistorical = normalizeAsOf(asOf) != null;
    // Pending values are overlaid BEFORE classification, so a queued value on a
    // persisted-empty attribute is a filled row and never hides behind the fold.
    const pendingValues = isHistorical ? undefined : editing?.pending;
    const effectiveProperties = useMemo(() => {
        if (!pendingValues || pendingValues.size === 0) return properties;
        const out: Record<string, unknown> = { ...properties };
        for (const [k, p] of pendingValues) out[k] = p.value;
        return out;
    }, [properties, pendingValues]);
    const rows = useMemo(
        () => buildRows(effectiveProperties, fields, new Set(suppressKeys)),
        [effectiveProperties, fields, suppressKeys],
    );
    const filled = rows.filter((r) => r.kind !== 'empty');
    const empty = rows.filter((r) => r.kind === 'empty');
    const visible = showEmpty ? rows : filled;
    // Shared "is historical" predicate (#6): empty/unparseable asOf == live.
    // #2076 follow-up — field-hint badges show ONLY while every field is
    // visible: either the empty-fields fold is open, or there is nothing
    // to fold (no empty fields ⇒ the panel is trivially unfolded).
    const unfolded = showEmpty || empty.length === 0;
    const language = i18n.resolvedLanguage ?? i18n.language;

    if (rows.length === 0) {
        return <p className="property-grid__none">Keine Eigenschaften vorhanden</p>;
    }

    return (
        <TooltipProvider delayDuration={300}>
            <div className={`property-grid${isHistorical ? ' property-grid--asof' : ''}`}>
                <dl className="property-grid__list">
                    {visible.map((row) => {
                        const meta = historyByAttribute?.get(row.key);
                        const hasHistory = meta?.canReveal ?? false;
                        // #2791 — provenance is a LIVE affordance, not only a
                        // time-travel one: "which system produced this value?"
                        // is the object view's natural question. The raw
                        // ``source_id`` is overloaded (document id OR
                        // source-system token), so it is resolved rather than
                        // printed, and an unresolvable one renders NOTHING —
                        // never a fallback badge, never the entity-level
                        // ``source_doc_ids`` union (wrong precisely on the
                        // converged multi-source entities this exists for).
                        // Only FILLED rows carry a badge — a source next to the
                        // "—" of an empty row labels an absence, not a value.
                        const provenance =
                            row.kind === 'empty'
                                ? null
                                : resolveProvenanceSource(meta?.provenanceSourceId, docsById, sourceSystems);
                        // A document is previewable only with a handler wired.
                        // Without one it renders NOTHING — the pre-#2791
                        // contract — rather than being demoted into the
                        // source-system badge, whose "Quelle: Beleg 2024" would
                        // be both the wrong class and the wrong sentence.
                        const previewDocId =
                            provenance?.kind === 'document' && onPreviewDocument ? provenance.documentId : null;
                        const hasProvenance = previewDocId != null || provenance?.kind === 'system';
                        // The derived-density chip is provenance of the VALUE, so it
                        // rides the provenance column (FR-018.7), not the value cell.
                        const density = row.key === DENSITY_ATTRIBUTE ? readPlantingDensity(effectiveProperties) : null;
                        const estimated = density?.estimated === true && density.unit != null;
                        const hint = unfolded ? pickFieldDescription(row.field, language) : undefined;
                        const pendingEdit = isHistorical ? undefined : editing?.pending?.get(row.key);
                        const editable = !!editing && !isHistorical && !!row.field && editing.canEdit({ key: row.key, field: row.field });
                        const isEditing = editable && editSession?.key === row.key;
                        const mySession = editSession?.session ?? 0;
                        return (
                            <div
                                key={row.key}
                                className={`property-grid__row${row.kind === 'empty' ? ' property-grid__row--empty' : ''}`}
                            >
                                <dt className="property-grid__label">
                                    {row.label}
                                    {hint && (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    type="button"
                                                    className="property-grid__hint"
                                                    aria-label={t('farm.fieldHint', { label: row.label })}
                                                >
                                                    i
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent className="property-grid__hint-tip">{hint}</TooltipContent>
                                        </Tooltip>
                                    )}
                                    {hasHistory && onAttributeHistory && (
                                        <button
                                            type="button"
                                            className="property-grid__history-btn"
                                            onClick={() => onAttributeHistory(row.key)}
                                            aria-label={`Vorherigen Wert anzeigen: ${row.label}`}
                                            title="Vorherigen Wert anzeigen"
                                        >
                                            <History size={13} />
                                        </button>
                                    )}
                                </dt>
                                <dd className="property-grid__cell">
                                    {isEditing && row.field ? (
                                        <InlineValueEditor
                                            key={mySession}
                                            field={row.field}
                                            initialValue={row.value}
                                            label={row.label}
                                            valueLabels={valueLabels?.[row.key]}
                                            onSave={async (value) => {
                                                try {
                                                    await editing!.onSave(row.key, value);
                                                } catch (err) {
                                                    // Editor still the live one → InlineValueEditor shows it inline.
                                                    if (openSessionRef.current?.session !== mySession) editing!.onOrphanedError?.(row.key, err);
                                                    throw err;
                                                }
                                                if (openSessionRef.current?.session === mySession) refocusKeyRef.current = row.key;
                                                setEditSession((cur) => (cur && cur.session === mySession ? null : cur));
                                            }}
                                            onCancel={onCancelEdit}
                                        />
                                    ) : (
                                        <>
                                            <ValueCell
                                                row={row}
                                                valueLabels={valueLabels}
                                                properties={effectiveProperties}
                                                onOpenTerm={onOpenTerm}
                                            />
                                            {pendingEdit && (
                                                <span className="property-grid__pending" role="status">
                                                    {t(pendingEdit.stale ? 'farm.edit.pendingStale' : 'farm.edit.pending')}
                                                </span>
                                            )}
                                            {editable && (
                                                <button
                                                    type="button"
                                                    className="property-grid__edit"
                                                    ref={(el) => {
                                                        if (el) badgeRefs.current.set(row.key, el);
                                                        else badgeRefs.current.delete(row.key);
                                                    }}
                                                    aria-label={t('farm.edit.badge', { label: row.label })}
                                                    title={t('farm.edit.badge', { label: row.label })}
                                                    onClick={() => {
                                                        sessionRef.current += 1;
                                                        setEditSession({ key: row.key, session: sessionRef.current });
                                                    }}
                                                >
                                                    <Pencil size={12} aria-hidden />
                                                </button>
                                            )}
                                        </>
                                    )}
                                </dd>
                                <div className="property-grid__provenance-col">
                                    {previewDocId && onPreviewDocument ? (
                                        // A real linked document keeps the
                                        // preview thread. A system token never
                                        // reaches it — that call could not
                                        // resolve (criterion 3).
                                        <button
                                            type="button"
                                            className="property-grid__provenance"
                                            onClick={() => onPreviewDocument(previewDocId)}
                                            title="Beleg zu diesem Wert öffnen"
                                        >
                                            {provenance?.label}
                                            {/* FR-017.7 — what the document asserts about this
                                                object, from the same Belege rows (`generic`
                                                is already filtered out server-side). */}
                                            {docLinkTypes(docsById, previewDocId)}
                                        </button>
                                    ) : provenance?.kind === 'system' ? (
                                        // role+aria-label so assistive tech reads
                                        // "Quelle: myfarm24" rather than running
                                        // the bare token onto the value ("0,54 ha
                                        // myfarm24"). The visible text stays the
                                        // label alone — the badge must stay small.
                                        <span
                                            className="property-grid__source"
                                            role="note"
                                            aria-label={`Quelle: ${provenance.label}`}
                                            title={`Quelle: ${provenance.label}`}
                                        >
                                            {provenance.label}
                                        </span>
                                    ) : null}
                                    {estimated && (
                                        <span
                                            className="property-grid__estimated"
                                            role="note"
                                            aria-label="Geschätzter Wert"
                                            title="Aus Pflanzenzahl und Fläche abgeleitet, nicht erfasst"
                                        >
                                            geschätzt
                                        </span>
                                    )}
                                    {!hasProvenance && !estimated && (
                                        <span className="property-grid__no-source" aria-hidden="true">
                                            —
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </dl>
                {empty.length > 0 && (
                    <button
                        type="button"
                        className="property-grid__toggle-empty"
                        onClick={() => setShowEmpty((v) => !v)}
                    >
                        <ChevronDown
                            size={14}
                            className={`property-grid__toggle-icon${showEmpty ? ' property-grid__toggle-icon--open' : ''}`}
                        />
                        {showEmpty ? 'Leere Felder ausblenden' : `${empty.length} leere Felder anzeigen`}
                    </button>
                )}
            </div>
        </TooltipProvider>
    );
};

export default PropertyGrid;
